import React, { useState } from 'react';
import { format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';

import './BetHistory.scss';
import { RootState } from '../../../../configs/store';
import BetHistoryRow from '../BetHistoryRow/BetHistoryRow';
import BetCombinations from '../BetCombinations/BetCombinations';
import { Bet, Stake } from '../../../../interfaces/BetslipState';
import Icon from 'src/components/Icon/Icon';
import { formatAmountToFixed } from 'src/utils/utils';

interface BetHistoryProp {
  item: {
    bets: Bet[],
    stakes: {
      combinations: Stake[]
    },
    numberOfBets?: number,
    code: string,
    ticketNumber: string,
    totalWinnings?:number,
    status: string,
    maximumProfit: number,
    profit: number,
    maximumReturn: number,
    potentialNetReturn: number,
    customerTax: number,
    return: number,
    netReturn: number,
    registerDate: number,
    evaluationDate: number,
    totalStake: number,
    totalReturn: number,
    totalReturnedStake: number,
    totalPotentialTax: number,
    paidOut: number,
    betType: number,
    settlingStatus: {
      totalBetsCount: number,
      settledBetsCount: number
    },
    tax: {
      taxType: string
    }
  },
  myBets: boolean,
  activeTab?: string,
  rowColor?: string,
  isTicketDetail?:boolean
}

const SLIP = {
  1: 'Single',
  2: 'Accumulator',
  3: 'System'
};

export default function BetHistory({ item, myBets = false, activeTab, rowColor = 'grey', isTicketDetail=false }: BetHistoryProp) {
  const [showBetDetails, setShowBetDetails] = useState(false);
  const [closeAll, setCloseAll] = useState(true);
  const { selectedTimezone } = useSelector((state: RootState) => state.odds);
  const registerDate = format(item.registerDate && utcToZonedTime(item.registerDate * 1000, selectedTimezone), 'dd/MM/yyyy HH:mm');
  const evaluationDate = activeTab === 'settled' && item.evaluationDate ?
    format(utcToZonedTime(item.evaluationDate * 1000, selectedTimezone), 'dd/MM/yyyy HH:mm') : '';
  const { t } = useTranslation();
  const status = item.paidOut > 0 ? 'won': _.camelCase(item.status);
  const isWin = item.tax.taxType === 'win';

  const checkTickeFooter = () => {
    if (isWin && item.status === 'open'){
      return  ['stake', 'potentialProfit', 'potentialReturn', 'potentialTax', 'potentialNetReturn'];
    } else if (item.status === 'cancelled'){
      return ['stake', 'return'];
    } else if (item.status && isWin){
      return ['stake', 'returnedStake', 'profit', 'return', 'customerTax', 'netReturn'];
    } else if (item.status === 'settled' && (!item.maximumReturn || item.maximumReturn === 0 || status !== 'won')){
      return ['stake'];
    } else if (item.status === 'settled' && !isWin){
      return ['stake', 'returnedStake', 'profit', 'return'];
    }
    return  ['totalStake', 'totalReturnedStake', 'profit', 'return', 'paidOut'];
  };

  const checkTicketKey = (label:string) => {
    if (label === 'stake'){
      return 'totalStake';
    } else if (label === 'potentialProfit'){
      return 'maximumProfit';
    } else if (label === 'potentialReturn'){
      return 'maximumReturn';
    } else if (label === 'potentialTax'){
      return 'totalPotentialTax';
    } else if (label === 'return'){
      return 'totalReturn';
    } else if (label === 'returnedStake'){
      return 'totalReturnedStake';
    } else if (label === 'customerTax'){
      return 'customerTax';
    }

    return label;
  };

  const betsFooterKeys = checkTickeFooter();

  const settlingStatus = `${ t('betHistory.settled') } (${ item.settlingStatus.settledBetsCount }/${ item.settlingStatus.totalBetsCount })`;
  const settleTabLabel = item?.settlingStatus.settledBetsCount >= 1
    ? `${ settlingStatus }: ${ evaluationDate }` : `${ t('betHistory.settled') }: ${ evaluationDate }`;
  const openBentsLabel = item?.settlingStatus.settledBetsCount >= 1 ? settlingStatus : '';

  const renderDateRegistered = () => {
    return (
      <div className="betting-history__info">
        <span className="betting-history__date">{ registerDate }</span>
        <span className="betting-history__date">
          { activeTab === 'openBets' || (isTicketDetail && !item.evaluationDate) ? openBentsLabel : settleTabLabel }
        </span>
        {
          status === 'cancelled' &&
          <span className="betting-history__date">
            { status }
          </span>
        }
      </div>
    );
  };

  const renderBetHistoryHeader = () => {
    const statusText = item.totalReturn > 0 ? `${ status } ${ formatAmountToFixed(item.totalReturn - item.customerTax) }` : status;

    return (
      <div
        onClick={ () => setCloseAll(true) }
        className="betting-history__header"
      >
        <span className="betting-history__number">{ `${ t('betHistory.nr') } ${ item.code }` }</span>
        <span className="betting-history__type">{ SLIP[item.betType] }</span>
        <span className="betting-history__open-lines"></span>
        {
          (activeTab === 'openBets' || (isTicketDetail && !item.evaluationDate)) && (
            <span
              className={
                classNames({
                  'betting-history__result': true,
                  'betting-history__result--open': true
                })
              }
            >
              { statusText }
            </span>
          )
        }
        {
          (activeTab === 'settled' || (isTicketDetail && item.evaluationDate)) && (
            <span
              className={
                classNames({
                  'betting-history__result': true,
                  [`betting-history__result--${ status }`]: status,
                  'betting-history__result--lost': item.totalReturn <= 0
                })
              }
            >
              { statusText }
              <Icon
                className="betting-history__icon-result"
                icon={ status }
              />
            </span>
          )
        }
        { renderDateRegistered() }
      </div>
    );
  };

  const renderBetHistoryMap = () => {
    const sortedBets = _.orderBy(item.bets, [(b) => b.tournament.code, 'eventCode'], ['desc']);

    const betMap = _.map(sortedBets, (bet, i) => (
      <BetHistoryRow
        key={ bet.eventName + bet.line + i }
        showHeader={ i === 0 || (sortedBets[i].eventCode !== sortedBets[i-1].eventCode) }
        showTournament={ i === 0 || (sortedBets[i].tournament.code !== sortedBets[i-1].tournament.code) }
        isTicketDetail={ isTicketDetail }
        isSettled={ activeTab !== 'openBets' }
        closeAll={ closeAll }
        bet={ bet }
      />
    ));

    return (
      <div className={
        classNames({
          'betting-history__body': true,
          'betting-history__body--white': rowColor === 'white'
        })
      }
      >
        { betMap }
      </div>
    );
  };

  const renderBetHistoryRow = (label, value) => {
    return (
      <div className="betting-history__stake">
        { t(`betHistory.${ label }`) }: { formatAmountToFixed(value) }
      </div>
    );
  };

  /* const renderOpenBets = () => {
    return (
      <>
        <div className="betting-history__container">
          { renderBetHistoryRow('totalStake', item.totalStake) }
          { isWin && renderBetHistoryRow('potencialTax', item.totalPotentialTax) }
        </div>
        <div className="betting-history__container">
          { renderBetHistoryRow('potencialProfit', item.maximumProfit) }
          { isWin && renderBetHistoryRow('potencialNetReturn', item.potentialNetReturn) }
        </div>
        <div className="betting-history__container">
          {  renderBetHistoryRow('potencialReturn', item.maximumReturn) }
        </div>
      </>
    );
  }; */

  const renderOpenBets = () => {
    return (
      <>
        <div className="betting-history__container">
          { renderBetHistoryRow('totalStake', item.totalStake) }
          { isWin && renderBetHistoryRow('potentialTax', item.totalPotentialTax) }
        </div>
        <div className="betting-history__container">
          { renderBetHistoryRow('potencialProfit', item.maximumProfit) }
          { isWin && renderBetHistoryRow('potencialNetReturn', item.potentialNetReturn) }
        </div>
        {  renderBetHistoryRow('potencialReturn', item.maximumReturn) }
      </>
    );
  };

  const renderSettledBets = () => {
    return (
      <>
        {
          _.map(betsFooterKeys, (key, index) => {
            let label = key === 'customerTax' ? 'tax' : key;

            if (key === 'paidOut'){
              label = 'paid';
            } else if (key === 'totalReturnedStake'){
              label ='returnedStake';
            }

            const newKey = checkTicketKey(key);
            return (
              <div className="betting-history__container" key={ index }>
                { renderBetHistoryRow(label, item[newKey]) }
              </div>
            );
          })
        }
      </>
    );
  };

  return (
    <ul>
      { renderBetHistoryHeader() }
      { renderBetHistoryMap() }
      <div
        onClick={ () => setShowBetDetails(!showBetDetails) }
        className="betting-history__footer"
      >
        { activeTab === 'openBets' ? renderOpenBets() : renderSettledBets() }
        <div
          className={
            classNames({
              'betting-history__bet-detail': true,
              'betting-history__bet-detail--open': showBetDetails || isTicketDetail
            })
          }
        >
          <div className="betting-history__number-bets">
            { t('betHistory.betsOnTickets') }: { item.numberOfBets }
          </div>
          <Icon
            className={
              classNames({
                'betting-history__arrow': true,
                'betting-history__arrow--open': showBetDetails
              })
            }
            icon="dropdownArrow"
          />
          { /* <Button className="betting-history__show-detail">
            { showBetDetails ? t('betHistory.hideDetails') : t('betHistory.showDetails') }
          </Button> */ }
        </div>
        { showBetDetails && <BetCombinations item={ item } betslip={ false } myBets={ myBets } /> }
        { /* <div className="betting-history__container">
          <button className="betting-history__cash-out betting-history__cash-out--disable">
            <span>Cash out 3.00</span>
          </button>
        </div> */ }
      </div>
    </ul>
  );
}
